/**
 * Helper for filtering and sorting loan tiers
 * Used by both LoanComparison and LoanHighlight components
 */

import { LenderTier } from '@/types';

export type ExtendedLenderTier = LenderTier & {
  lenderName?: string | null;
  lenderLogo?: string | null;
};

export type AprOverridable = {
  minApr?: number | null;
  maxApr?: number | null;
  overrideApr?: boolean | null;
  overrideMinApr?: number | null;
  overrideMaxApr?: number | null;
};

export interface FilterParameters {
  purposes?: string[];
  productTypes?: string[];
  loanAmount?: [number | null, number | null];
  loanTerm?: [number | null, number | null];
  timeInBiz?: [number | null, number | null];
  monthlySales?: [number | null, number | null];
  apr?: [number | null, number | null];
  hideApr?: boolean;
}

function isValidNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

// hideApr remains authoritative for visibility; this only resolves the APR values used when APRs are shown.
export function applyAprOverride<T extends AprOverridable>(item: T): T {
  if (!item.overrideApr) return item;

  const minApr = item.overrideMinApr;
  const maxApr = item.overrideMaxApr;
  if (!isValidNumber(minApr) || !isValidNumber(maxApr) || minApr > maxApr) return item;

  return {
    ...item,
    minApr,
    maxApr,
  };
}

/**
 * Normalize string for comparison: lowercase, underscores/hyphens to spaces, collapse spaces
 */
function normalizeForComparison(value?: string | null): string {
  if (!value) return '';
  return value
    .replace(/[\/_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * Filter and sort loan tiers based on criteria
 * @param allTiers - All available tier records
 * @param filters - Filter parameters
 * @param sortBy - Sort option ('interestLowToHigh', 'amountHighToLow', 'providerAZ', 'providerZA')
 * @param limit - Maximum results to return (default: unlimited)
 * @returns Filtered and sorted tiers
 */
export function filterAndSortTiers(
  allTiers: ExtendedLenderTier[],
  filters: FilterParameters,
  sortBy: string = 'interestLowToHigh',
  limit?: number
): ExtendedLenderTier[] {
  let result = [...allTiers];
  const num = (v: any) => Number(v || 0);

  // Filter by lenders
  if (filters.purposes && filters.purposes.length > 0) {
    const normalize = (v: string) => normalizeForComparison(v);
    result = result.filter((t) =>
      (t.purposeAllowed || [])
        .map(normalize)
        .some((p: any) => filters.purposes!.map(normalize).includes(p))
    );
  }

  // Filter by product types
  if (filters.productTypes && filters.productTypes.length > 0) {
    const normalize = (v: string) => normalizeForComparison(v);
    result = result.filter((t) => {
      const tierProductTypes = ('productTypesAllowed' in t ? t.productTypesAllowed || [] : []).map(
        normalize
      );
      const selectedProductTypes = filters.productTypes!.map(normalize);
      return tierProductTypes.some((p: string) => selectedProductTypes.includes(p));
    });
  }

  // Filter by ranges
  if (filters.loanAmount) {
    result = result.filter((t) => {
      return (
        num(t.minLoanAmount) <= num(filters.loanAmount![1] ?? Infinity) &&
        num(t.maxLoanAmount) >= num(filters.loanAmount![0] ?? 0)
      );
    });
  }

  if (filters.loanTerm) {
    result = result.filter((t) => {
      return (
        num(t.minLoanTerm) <= num(filters.loanTerm![1] ?? Infinity) &&
        num(t.maxLoanTerm) >= num(filters.loanTerm![0] ?? 0)
      );
    });
  }

  if (filters.timeInBiz) {
    result = result.filter((t) => {
      return (
        num(t.minTimeInBiz) <= num(filters.timeInBiz![1] ?? Infinity) &&
        num(t.maxTimeInBiz) >= num(filters.timeInBiz![0] ?? 0)
      );
    });
  }

  if (filters.monthlySales) {
    result = result.filter((t) => {
      return (
        num(t.minMonthlySales) <= num(filters.monthlySales![1] ?? Infinity) &&
        num(t.maxMonthlySales) >= num(filters.monthlySales![0] ?? 0)
      );
    });
  }

  if (filters.apr) {
    result = result.filter((t) => {
      return (
        num(t.minApr) <= num(filters.apr![1] ?? Infinity) &&
        num(t.maxApr) >= num(filters.apr![0] ?? 0)
      );
    });
  }

  if (filters.hideApr) {
    result = result.filter((t) => !t.hideApr);
  }

  // Sort
  switch (sortBy) {
    case 'interestLowToHigh': {
      result.sort((a, b) => {
        const aApr = Number(a.minApr);
        const bApr = Number(b.minApr);
        const aValid = !isNaN(aApr) && aApr > 0;
        const bValid = !isNaN(bApr) && bApr > 0;

        if (aValid && bValid) return aApr - bApr;
        if (!aValid && bValid) return 1;
        if (aValid && !bValid) return -1;
        return 0;
      });
      break;
    }
    case 'amountHighToLow':
      result.sort((a, b) => (num(b.maxLoanAmount) || 0) - (num(a.maxLoanAmount) || 0));
      break;
    case 'providerAZ':
      result.sort((a, b) => (a.lenderName || '').localeCompare(b.lenderName || ''));
      break;
    case 'providerZA':
      result.sort((a, b) => (b.lenderName || '').localeCompare(a.lenderName || ''));
      break;
    default:
      break;
  }

  return limit ? result.slice(0, limit) : result;
}
